import { Box, Card, CardActionArea, CardContent, Skeleton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  STATUS_META,
  STATUS_ORDER,
  type EventInboxStats,
  type EventInboxStatus,
} from '../eventInboxTypes';

interface EventInboxStatsBarProps {
  stats: EventInboxStats | null;
  loading?: boolean;
  /** Currently active status filter (drives `aria-pressed`), or undefined. */
  activeStatus?: EventInboxStatus;
  /** Toggle a status filter from a KPI card. */
  onSelectStatus: (status: EventInboxStatus) => void;
  /** Clear the status filter (Total card). */
  onClearStatus: () => void;
}

const STAT_KEY: Record<EventInboxStatus, keyof EventInboxStats> = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  DONE: 'done',
  FAILED: 'failed',
};

/**
 * KPI bar: one clickable card per status plus a Total card. Clicking a status
 * card toggles the corresponding `status[in]` filter; Total clears it. The
 * FAILED card is visually emphasised (tinted background + error border) so
 * failures draw the eye. Cards are keyboard-operable (CardActionArea →
 * role=button, tabIndex, Enter/Space) and expose `aria-pressed`.
 */
export function EventInboxStatsBar({
  stats,
  loading = false,
  activeStatus,
  onSelectStatus,
  onClearStatus,
}: EventInboxStatsBarProps) {
  const theme = useTheme();
  const { t } = useTranslation('eventInbox');

  // FAILED breakdown by rule, most frequent first; 'unknown' → localized label.
  const failedRuleBreakdown = Object.entries(stats?.by_failed_rule ?? {})
    .sort(([, a], [, b]) => b - a)
    .map(([rule, count]): [string, number] => [
      rule === 'unknown' ? t('stats.unknownRule') : rule,
      count,
    ]);

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
        mb: 3,
      }}
    >
      {STATUS_ORDER.map(status => {
        const meta = STATUS_META[status];
        const palette = theme.palette[meta.color];
        const isActive = activeStatus === status;
        const isFailed = status === 'FAILED';
        const value = stats ? stats[STAT_KEY[status]] : 0;

        return (
          <Card
            key={status}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: isActive
                ? palette.main
                : isFailed
                  ? alpha(theme.palette.error.main, 0.4)
                  : theme.palette.divider,
              bgcolor: isFailed ? alpha(theme.palette.error.main, 0.06) : 'background.paper',
              boxShadow: isActive ? `0 0 0 2px ${alpha(palette.main, 0.35)}` : 'none',
              transition: 'box-shadow 0.2s, border-color 0.2s',
            }}
          >
            <CardActionArea
              onClick={() => onSelectStatus(status)}
              aria-pressed={isActive}
              aria-label={t('stats.filterBy', { status: t(meta.labelKey) })}
              sx={{ height: '100%', borderRadius: 3 }}
            >
              <CardContent sx={{ py: 2, px: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: palette.main,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.label,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontWeight: 600,
                    }}
                  >
                    {t(meta.labelKey)}
                  </Typography>
                </Box>
                {loading ? (
                  <Skeleton width={48} height={40} />
                ) : (
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: isFailed ? palette.dark : theme.palette.text.body,
                    }}
                  >
                    {value.toLocaleString()}
                  </Typography>
                )}

                {/* FAILED breakdown by rule (stats.by_failed_rule) */}
                {isFailed && !loading && failedRuleBreakdown.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {failedRuleBreakdown.map(([rule, count]) => (
                      <Box
                        key={rule}
                        sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                      >
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ color: theme.palette.text.label, maxWidth: 140 }}
                          title={rule}
                        >
                          {rule}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.error.dark, fontWeight: 600 }}
                        >
                          {count.toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </CardActionArea>
          </Card>
        );
      })}

      {/* Total — clears the status filter */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: !activeStatus ? theme.palette.primary.main : theme.palette.divider,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          boxShadow: !activeStatus ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}` : 'none',
          transition: 'box-shadow 0.2s, border-color 0.2s',
        }}
      >
        <CardActionArea
          onClick={onClearStatus}
          aria-pressed={!activeStatus}
          aria-label={t('stats.showAll')}
          sx={{ height: '100%', borderRadius: 3 }}
        >
          <CardContent sx={{ py: 2, px: 2.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.label,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 600,
                display: 'block',
                mb: 0.5,
              }}
            >
              {t('stats.total')}
            </Typography>
            {loading ? (
              <Skeleton width={48} height={40} />
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {(stats?.total ?? 0).toLocaleString()}
              </Typography>
            )}
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  );
}
